import { useCallback } from 'react';

import { useNovelRepository } from '@/hooks/useNovelRepository';
import type { EntitySummary } from '@/lib/types';

import { useEntityAccessLayer } from '../../shared/hooks/use-entity-access-layer';
import { mapLocationDTOToDomain } from '../api/mappers';
import type { LocationFormValues } from '../schemas';
import type { Location } from '../types';

/**
 * Fields on the Location that are mirrored into the EntitySummary.
 * When any of these change, the summary is automatically updated.
 */
const LOCATION_SUMMARY_FIELDS = [
  'name',
  'type',
  'shortDescription',
  'mainImageUrl',
  'mainImagePath',
  'parentId',
] as const;

/**
 * Centralised access layer for locations.
 *
 * @example
 * ```tsx
 * const {
 *   summaries: locations,
 *   location,
 *   sections,
 *   updateItem: updateLocation,
 *   deleteItem: deleteLocation,
 *   isLoading,
 * } = useLocationAccessLayer(locationId);
 * ```
 */
export function useLocationAccessLayer(locationId?: string) {
  const {
    summaries,
    item,
    sections,
    isLoading,
    error,
    updateItem,
    deleteItem,
    getNewId,
  } = useEntityAccessLayer<Location, EntitySummary>({
    entityType: 'locations',
    summaryFieldKeys: LOCATION_SUMMARY_FIELDS,
    itemId: locationId,
    itemMapper: mapLocationDTOToDomain,
  });

  const {
    saveItem,
    updateEntitySummary,
  } = useNovelRepository();

  /**
   * Creates a new location with initial data + summary.
   * NOTE: Image upload is NOT handled here — call this AFTER
   * images have been processed.
   */
  const createLocation = useCallback(
    async (id: string, data: LocationFormValues) => {
      const now = new Date().toISOString();
      const newLocation = {
        ...data,
        id,
        audit: { createdAt: now, updatedAt: now },
      };

      // Persist item
      await saveItem('entities', 'locations', newLocation);

      // Extract summary fields with safe string coercion
      const name = String(data.name ?? '');
      const type = String(data.type ?? '');
      const shortDescription = String(data.shortDescription ?? '');
      const mainImageUrl = String(data.mainImageUrl ?? '');
      const mainImagePath = String((data as Record<string, unknown>).mainImagePath ?? '');
      const primaryImageId = typeof data.primaryImageId === 'string' ? data.primaryImageId : null;
      const parentId = String(data.parentId ?? '');

      // Write summary
      await updateEntitySummary('locations', id, {
        id,
        name,
        type,
        shortDescription,
        mainImageUrl,
        mainImagePath,
        primaryImageId,
        parentId,
        updatedAt: now,
      });
    },
    [saveItem, updateEntitySummary],
  );

  return {
    /** Location summary list, sorted by updatedAt DESC. */
    locations: summaries,
    /** Single location domain model (if ID provided). */
    location: item,
    /** Location UI settings (sections/fields). */
    sections,
    isLoading,
    error,

    /** Partial-update a location. Auto-syncs summary if needed. */
    updateLocation: updateItem,

    /** Delete a location, its summary, relationships, and map references. */
    deleteLocation: deleteItem,

    /** Create a new location (after image processing). */
    createLocation,

    /** Generate a Firestore-compatible ID for a new location. */
    getNewId,
  };
}
